import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  describeWeather,
  FORECAST_LENGTHS,
  MAX_WEATHER_PLACES,
  placeLabel,
  WEATHER_VIEWS,
  WEATHER_VIEW_LABELS,
  type ForecastLength,
  type WeatherAlert,
  type WeatherPlace,
  type WeatherReport,
  type WeatherView,
} from '@td/shared';
import { get } from '../lib/api';
import { WeatherScene } from './WeatherScene';
import { Doodle } from './Doodle';

/**
 * A weather card.
 *
 * Several places, a view for each, and its own settings — because two of
 * these on one board should be able to show two different towns without one
 * fighting the other. Configuration lives beside the dashboard layout in
 * local storage: it is a per-board display preference, and a card pinned to a
 * classroom monitor should not change because someone opened the dashboard on
 * a laptop at home.
 */

export interface WeatherConfig {
  places: WeatherPlace[];
  /** Index into `places`. */
  active: number;
  view: WeatherView;
  days: ForecastLength;
  units: 'f' | 'c';
  /** Rotate through the saved places on a timer, like an airport board. */
  rotate: boolean;
}

export const DEFAULT_WEATHER_CONFIG: WeatherConfig = {
  places: [],
  active: 0,
  view: 'now',
  days: 7,
  units: 'f',
  rotate: false,
};

function configKey(slot: string) {
  return `td.weather.${slot}`;
}

export function readWeatherConfig(slot: string): WeatherConfig {
  try {
    const raw = JSON.parse(localStorage.getItem(configKey(slot)) ?? 'null') as WeatherConfig | null;
    if (!raw?.places) return { ...DEFAULT_WEATHER_CONFIG };
    return { ...DEFAULT_WEATHER_CONFIG, ...raw, places: raw.places.slice(0, MAX_WEATHER_PLACES) };
  } catch {
    return { ...DEFAULT_WEATHER_CONFIG };
  }
}

function writeWeatherConfig(slot: string, config: WeatherConfig) {
  try {
    localStorage.setItem(configKey(slot), JSON.stringify(config));
  } catch {
    // Not remembered; it still holds for this session.
  }
}

function useWeather(place: WeatherPlace | undefined, units: 'f' | 'c') {
  return useQuery({
    queryKey: ['weather', place?.latitude, place?.longitude, units],
    queryFn: () => {
      const params = new URLSearchParams({
        lat: String(place!.latitude),
        lon: String(place!.longitude),
        name: place!.name,
        admin1: place!.admin1 ?? '',
        country: place!.country,
        countryCode: place!.countryCode,
        timezone: place!.timezone,
        id: String(place!.id),
        units,
      });
      return get<WeatherReport>(`/weather?${params}`);
    },
    enabled: !!place,
    // The server caches for ten minutes, so asking more often achieves
    // nothing but noise. Refetched when the window comes back into focus.
    staleTime: 5 * 60 * 1000,
    // The server caches for ten minutes; asking every ten keeps what is on
    // screen within one cache period of the truth without ever being noise.
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

export function WeatherWidget({
  slot,
  accent,
}: {
  slot: string;
  accent: { base: string; deep: string; tint: string };
}) {
  const [config, setConfig] = useState(() => readWeatherConfig(slot));
  const [editing, setEditing] = useState(false);

  useEffect(() => writeWeatherConfig(slot, config), [slot, config]);

  const place = config.places[config.active] ?? config.places[0];
  const { data, isLoading, error, refetch, isFetching } = useWeather(place, config.units);

  /** An airport board, for a card left up on the classroom projector. */
  useEffect(() => {
    if (!config.rotate || config.places.length < 2) return;

    const timer = setInterval(() => {
      setConfig((c) => ({ ...c, active: (c.active + 1) % c.places.length }));
    }, 12_000);

    return () => clearInterval(timer);
  }, [config.rotate, config.places.length]);

  if (config.places.length === 0 || editing) {
    return (
      <WeatherSettings
        config={config}
        onChange={setConfig}
        onDone={() => setEditing(false)}
        canClose={config.places.length > 0}
        accent={accent}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {config.places.map((p, i) => (
            <button
              key={`${p.id}-${i}`}
              onClick={() => setConfig((c) => ({ ...c, active: i }))}
              aria-pressed={i === config.active}
              className={[
                'max-w-[10rem] truncate rounded-lg px-2 py-1 text-xs transition-colors',
                i === config.active ? 'accent-fill' : 'text-walnut hover:bg-linen',
              ].join(' ')}
            >
              {p.name}
            </button>
          ))}
        </div>

        <button
          onClick={() => setEditing(true)}
          className="btn-soft shrink-0"
          aria-label="Weather settings"
        >
          Places
        </button>
      </div>

      {isLoading && <p className="text-sm text-stone">Looking outside…</p>}

      {error && (
        <div className="rounded-lg border border-rust/40 bg-rose/10 p-3 text-sm text-rust">
          <p>{error instanceof Error ? error.message : 'The forecast could not be fetched'}</p>
          <button onClick={() => void refetch()} className="btn-soft mt-2">
            Try again
          </button>
        </div>
      )}

      {data && (
        <div className="min-h-0 flex-1 overflow-auto">
          {data.alerts.map((alert) => (
            <AlertBanner key={alert.id} alert={alert} />
          ))}

          {data.alerts.length === 0 && data.headsUp.length > 0 && (
            <HeadsUp report={data} accent={accent} />
          )}

          {config.view === 'now' && <RightNow report={data} />}
          {config.view === 'hourly' && <Hourly report={data} accent={accent} />}
          {config.view === 'days' && <Days report={data} days={config.days} accent={accent} />}
        </div>
      )}

      <div className="mt-2 flex shrink-0 flex-wrap items-center gap-1">
        {WEATHER_VIEWS.map((v) => (
          <button
            key={v}
            onClick={() => setConfig((c) => ({ ...c, view: v }))}
            aria-pressed={config.view === v}
            className={[
              'rounded-lg px-2 py-1 text-[11px] transition-colors',
              config.view === v ? 'accent-fill' : 'text-walnut hover:bg-linen',
            ].join(' ')}
          >
            {WEATHER_VIEW_LABELS[v]}
          </button>
        ))}

        {config.view === 'days' && (
          <select
            className="ml-1 rounded-lg border border-sand bg-white px-1.5 py-1 text-[11px] text-walnut"
            value={config.days}
            onChange={(e) =>
              setConfig((c) => ({ ...c, days: Number(e.target.value) as ForecastLength }))
            }
            aria-label="How many days"
          >
            {FORECAST_LENGTHS.map((n) => (
              <option key={n} value={n}>
                {n} days
              </option>
            ))}
          </select>
        )}

        <span className="ml-auto text-[10px] text-stone">
          {isFetching ? 'updating…' : data ? timeAgo(data.fetchedAt) : ''}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ views */

function RightNow({ report }: { report: WeatherReport }) {
  const { current } = report;
  const { label, scene } = describeWeather(current.code);
  const unit = report.units === 'f' ? '°F' : '°C';
  const speed = report.units === 'f' ? 'mph' : 'km/h';
  const today = report.daily[0];

  return (
    <div className="flex flex-wrap items-center gap-3">
      <WeatherScene scene={scene} isDay={current.isDay} size={104} />

      <div className="min-w-0">
        <p className="font-display text-4xl leading-none text-espresso">
          {current.temperature}
          <span className="text-xl text-walnut">{unit}</span>
        </p>
        <p className="mt-1 text-sm text-walnut">{label}</p>
        <p className="text-xs text-stone">
          Feels like {current.feelsLike}
          {unit}
          {today ? ` · ${today.high}° / ${today.low}°` : ''}
        </p>
        <p className="mt-1 text-xs text-stone">
          Wind {current.windSpeed} {speed}
          {current.windGust > current.windSpeed + 5 ? `, gusting ${current.windGust}` : ''} ·{' '}
          {current.humidity}% humidity
        </p>
      </div>
    </div>
  );
}

function Hourly({
  report,
  accent,
}: {
  report: WeatherReport;
  accent: { base: string; deep: string };
}) {
  const hours = report.hourly.slice(0, 24);
  const unit = report.units === 'f' ? '°' : '°';

  return (
    <div className="scroll-x pb-1">
      <div className="flex gap-1">
        {hours.map((h, i) => {
          const { scene, label } = describeWeather(h.code);
          return (
            <div
              key={h.time}
              title={`${label} · ${h.precipitationChance}% chance of precipitation`}
              className="flex w-14 shrink-0 flex-col items-center rounded-lg px-1 py-1.5"
              style={{ backgroundColor: i === 0 ? accent.base + '22' : undefined }}
            >
              <span className="text-[10px] text-stone">{i === 0 ? 'Now' : hourLabel(h.time)}</span>
              <WeatherScene scene={scene} isDay={h.isDay} size={34} />
              <span className="text-xs text-espresso">
                {h.temperature}
                {unit}
              </span>
              {h.precipitationChance >= 20 && (
                <span className="text-[10px]" style={{ color: accent.deep }}>
                  {h.precipitationChance}%
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Days({
  report,
  days,
  accent,
}: {
  report: WeatherReport;
  days: number;
  accent: { base: string; deep: string };
}) {
  const list = report.daily.slice(0, days);

  // One scale for the whole run, so the bars can be compared to each other.
  const low = Math.min(...list.map((d) => d.low));
  const high = Math.max(...list.map((d) => d.high));
  const span = Math.max(high - low, 1);

  return (
    <ul className="space-y-0.5">
      {list.map((d, i) => {
        const { scene, label } = describeWeather(d.code);
        return (
          <li key={d.date} className="flex items-center gap-2 rounded-lg px-1 py-0.5">
            <span className="w-10 shrink-0 text-xs text-walnut">
              {i === 0 ? 'Today' : dayLabel(d.date)}
            </span>

            <span className="shrink-0" title={label}>
              <WeatherScene scene={scene} size={30} />
            </span>

            <span className="w-8 shrink-0 text-right text-[10px]" style={{ color: accent.deep }}>
              {d.precipitationChance >= 20 ? `${d.precipitationChance}%` : ''}
            </span>

            <span className="w-7 shrink-0 text-right text-xs text-stone">{d.low}°</span>

            {/* Where this day's range sits inside the week's range. */}
            <span className="h-1.5 min-w-0 flex-1 rounded-full bg-sand/60">
              <span
                className="block h-full rounded-full"
                style={{
                  marginLeft: `${((d.low - low) / span) * 100}%`,
                  width: `${Math.max(((d.high - d.low) / span) * 100, 6)}%`,
                  backgroundColor: accent.base,
                }}
              />
            </span>

            <span className="w-7 shrink-0 text-xs text-espresso">{d.high}°</span>
          </li>
        );
      })}
    </ul>
  );
}

/* ----------------------------------------------------------------- alerts */

const SEVERITY_STYLE: Record<string, { border: string; background: string; text: string }> = {
  Extreme: { border: '#9A6045', background: '#C08B8B33', text: '#9A6045' },
  Severe: { border: '#B07C63', background: '#D9A85C2E', text: '#9A6045' },
  Moderate: { border: '#D9A85C', background: '#D9A85C22', text: '#8E6829' },
  Minor: { border: '#A3B18A', background: '#A3B18A22', text: '#5F6B48' },
  Unknown: { border: '#C4A287', background: '#DCCDBB33', text: '#6B5D52' },
};

function AlertBanner({ alert }: { alert: WeatherAlert }) {
  const [open, setOpen] = useState(false);
  const style = SEVERITY_STYLE[alert.severity] ?? SEVERITY_STYLE.Unknown!;

  return (
    <div
      className="mb-2 rounded-lg border p-2"
      style={{ borderColor: style.border, backgroundColor: style.background }}
      role={alert.severity === 'Extreme' || alert.severity === 'Severe' ? 'alert' : undefined}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 text-left"
      >
        <span className="mt-0.5 shrink-0" style={{ color: style.text }}>
          <Doodle name="lightbulb" size={15} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold" style={{ color: style.text }}>
            {alert.event}
          </span>
          <span className="block text-[11px] leading-snug text-walnut">
            {alert.headline || alert.event}
          </span>
        </span>
      </button>

      {open && (
        <div className="mt-2 max-h-40 overflow-auto text-[11px] leading-relaxed text-walnut">
          <p className="whitespace-pre-line">{alert.description}</p>
          {alert.instruction && (
            <p className="mt-2 whitespace-pre-line font-medium">{alert.instruction}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The unofficial warning.
 *
 * Read from the forecast rather than waiting for the weather service, which
 * often issues an alert an hour before it hits and sometimes not at all. For
 * planning whether recess happens, "storms about 2 PM" beats a siren.
 */
function HeadsUp({
  report,
  accent,
}: {
  report: WeatherReport;
  accent: { base: string; deep: string };
}) {
  return (
    <div
      className="mb-2 rounded-lg border px-2 py-1.5"
      style={{ borderColor: accent.base, backgroundColor: accent.base + '18' }}
    >
      {report.headsUp.slice(0, 2).map((h) => (
        <p key={h.kind} className="text-[11px] leading-snug" style={{ color: accent.deep }}>
          {h.message} {whenLabel(h.time)}
        </p>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- settings */

function WeatherSettings({
  config,
  onChange,
  onDone,
  canClose,
  accent,
}: {
  config: WeatherConfig;
  onChange: (config: WeatherConfig) => void;
  onDone: () => void;
  canClose: boolean;
  accent: { base: string; deep: string };
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WeatherPlace[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Searched as you type, a beat after you stop. A request per keystroke
  // would be four times the traffic for the same answer.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(() => {
      setSearching(true);
      setError(null);
      get<WeatherPlace[]>(`/weather/search?q=${encodeURIComponent(q)}`)
        .then(setResults)
        .catch(() => setError('That search could not be run'))
        .finally(() => setSearching(false));
    }, 350);

    return () => clearTimeout(timer);
  }, [query]);

  function add(place: WeatherPlace) {
    if (config.places.length >= MAX_WEATHER_PLACES) return;
    // Same town twice is never intended.
    if (config.places.some((p) => p.id === place.id)) return;

    onChange({ ...config, places: [...config.places, place], active: config.places.length });
    setQuery('');
    setResults([]);
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm text-espresso">Weather places</h3>
        {canClose && (
          <button onClick={onDone} className="btn-soft-accent">
            Done
          </button>
        )}
      </div>

      {config.places.length > 0 && (
        <ul className="mb-2 space-y-1">
          {config.places.map((p, i) => (
            <li key={`${p.id}-${i}`} className="flex items-center gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate text-espresso">{placeLabel(p)}</span>
              <button
                onClick={() =>
                  onChange({
                    ...config,
                    places: config.places.filter((_, j) => j !== i),
                    active: 0,
                  })
                }
                className="btn-soft-danger"
                aria-label={`Remove ${p.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {config.places.length < MAX_WEATHER_PLACES && (
        <>
          <input
            className="field mb-1 text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a town — Elkhart, Fort Wayne, Columbia City…"
            aria-label="Search for a place"
          />

          {searching && <p className="text-xs text-stone">Searching…</p>}
          {error && <p className="text-xs text-rust">{error}</p>}

          <ul className="min-h-0 flex-1 overflow-auto">
            {results.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => add(r)}
                  className="w-full rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-linen"
                >
                  <span className="block text-espresso">{placeLabel(r)}</span>
                  <span className="block text-[10px] text-stone">{r.country}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-2 flex shrink-0 flex-wrap items-center gap-2 border-t border-sand/60 pt-2">
        <div className="flex overflow-hidden rounded-lg border border-sand">
          {(['f', 'c'] as const).map((u) => (
            <button
              key={u}
              onClick={() => onChange({ ...config, units: u })}
              aria-pressed={config.units === u}
              className={[
                'px-2 py-1 text-[11px] transition-colors',
                config.units === u ? 'accent-fill' : 'bg-white text-walnut hover:bg-linen',
              ].join(' ')}
            >
              °{u.toUpperCase()}
            </button>
          ))}
        </div>

        {config.places.length > 1 && (
          <button
            onClick={() => onChange({ ...config, rotate: !config.rotate })}
            aria-pressed={config.rotate}
            title="Cycle through the places every few seconds"
            className={config.rotate ? 'btn-soft-accent' : 'btn-soft'}
            style={config.rotate ? { color: accent.deep } : undefined}
          >
            {config.rotate ? 'Rotating' : 'Rotate places'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ dates */

function hourLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: 'numeric' }).replace(' ', '');
}

function dayLabel(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' });
}

/** "in about 2 hours", or the clock time once it is further out. */
function whenLabel(iso: string): string {
  const then = new Date(iso).getTime();
  const hours = Math.round((then - Date.now()) / 3_600_000);

  if (hours <= 0) return 'now';
  if (hours === 1) return 'in about an hour';
  if (hours <= 6) return `in about ${hours} hours`;

  return `around ${new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric' })}`;
}

function timeAgo(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 min ago';
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)}h ago`;
}
